import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Card,
  Chip,
} from '@material-ui/core';
import {
  ArrowUpward as ArrowUpwardIcon,
  Person as PersonIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { User, AuthorizationLevelInfo } from '../../model';

const useStyles = makeStyles((theme) => ({
  widget: {
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: theme.spacing(2),
  },
  widgetHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  widgetTitle: {
    fontWeight: 500,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
  },
  widgetContent: {
    padding: theme.spacing(1.5),
  },
  chainItem: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.6),
    marginBottom: theme.spacing(0.6),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.default,
  },
  avatar: {
    width: theme.spacing(3.5),
    height: theme.spacing(3.5),
    marginRight: theme.spacing(1.2),
    fontSize: '0.75rem',
  },
  userInfo: {
    flex: 1,
  },
  levelIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(0.6),
  },
  arrow: {
    color: theme.palette.text.secondary,
    margin: theme.spacing(0, 0.8),
    fontSize: '0.8rem',
  },
  positionChip: {
    height: 16,
    fontSize: '0.6rem',  
    marginTop: theme.spacing(0.3),
  },
  currentUser: {
    backgroundColor: theme.palette.primary.light,
    '& .MuiTypography-root': {
      color: theme.palette.primary.contrastText,
    },
  },
  compactText: {
    fontSize: '0.85rem',
  },
  subtitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.2),
  },
  footerText: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.2),
  },
}));

interface ReportingChainViewProps {
  reportingChain: User[];
  authorizationLevels?: AuthorizationLevelInfo[];
  getAuthorizationLevelColor: (level: number) => string;
}

const ReportingChainView: React.FC<ReportingChainViewProps> = ({
  reportingChain,
  authorizationLevels = [],
  getAuthorizationLevelColor,
}) => {
  const classes = useStyles();

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAuthorizationLevelInfo = (authorizationLevel: any): AuthorizationLevelInfo | null => {
    if (!authorizationLevel) return null;
    
    // Try to find by numeric value first
    const numericValue = Number(authorizationLevel);
    if (!isNaN(numericValue)) {
      const levelByValue = authorizationLevels.find(l => l.value === numericValue);
      if (levelByValue) return levelByValue;
    }
    
    // Try to find by string name (for enum values like "STAFF")
    const levelByName = authorizationLevels.find(l => 
      l.name.toUpperCase() === String(authorizationLevel).toUpperCase()
    );
    if (levelByName) return levelByName;
    
    // Fallback for when authorizationLevels is not available
    const fallbackLevels = {
      1: 'Director', 2: 'Manager', 3: 'Supervisor', 
      4: 'Team Lead', 5: 'Operator', 6: 'Staff'
    };
    
    if (!isNaN(numericValue) && fallbackLevels[numericValue as keyof typeof fallbackLevels]) {
      return {
        value: numericValue,
        name: fallbackLevels[numericValue as keyof typeof fallbackLevels],
        description: ''
      };
    }
    
    // Try to match string against fallback levels
    const fallbackByName = Object.entries(fallbackLevels).find(([_, name]) => 
      name.toUpperCase() === String(authorizationLevel).toUpperCase()
    );
    if (fallbackByName) {
      return {
        value: parseInt(fallbackByName[0]),
        name: fallbackByName[1],
        description: ''
      };
    }
    
    return null;
  };

  if (reportingChain.length === 0) {
    return null;
  }

  // Reverse the chain to show from top-level manager down to the selected user
  const reversedChain = [...reportingChain].reverse();

  return (
    <Card className={classes.widget}>
      <Box className={classes.widgetHeader}>
        <Typography className={classes.widgetTitle}>
          <PersonIcon style={{ marginRight: 8, fontSize: '1rem' }} />
          Reporting Chain
        </Typography>
      </Box>
      <Box className={classes.widgetContent}>
        <Typography className={classes.subtitle}>
          From top-level manager to selected user
        </Typography>

        {reversedChain.map((user, index) => {
          const isCurrentUser = index === reversedChain.length - 1;
          
          return (
            <Box key={user.id}>
              {index > 0 && (
                <Box className={classes.levelIndicator}>
                  <ArrowUpwardIcon className={classes.arrow} />
                  <Typography variant="caption" color="textSecondary" style={{ fontSize: '0.7rem' }}>
                    reports to
                  </Typography>
                </Box>
              )}
              
              <Box className={`${classes.chainItem} ${isCurrentUser ? classes.currentUser : ''}`}>
                <Avatar className={classes.avatar}>
                  {getInitials(user.name)}
                </Avatar>
                
                <Box className={classes.userInfo}>
                  <Typography variant="subtitle2" component="div" className={classes.compactText}>
                    {user.name}
                    {isCurrentUser && (
                      <Chip
                        size="small"
                        label="Selected"
                        style={{
                          marginLeft: 8,
                          height: 16,
                          fontSize: '0.6rem',
                          backgroundColor: 'rgba(255,255,255,0.2)',
                          color: 'inherit',
                        }}
                      />
                    )}
                  </Typography>
                  
                  {user.authorizationLevel && (
                    <Chip
                      size="small"
                      label={getAuthorizationLevelInfo(user.authorizationLevel)?.name || 'Unknown'}
                      className={classes.positionChip}
                      style={{
                        backgroundColor: getAuthorizationLevelColor(getAuthorizationLevelInfo(user.authorizationLevel)?.value || 6),
                        color: 'white',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          );
        })}

        <Typography className={classes.footerText}>
          Hierarchy Depth: {reportingChain.length - 1} level{reportingChain.length - 1 !== 1 ? 's' : ''} from top
        </Typography>
      </Box>
    </Card>
  );
};

export default ReportingChainView;